import React, { useState, useEffect } from 'react';
import { Package, Users, CheckCircle2, Clock, CreditCard, MapPin, Navigation, ExternalLink, Truck, Bell, FileText, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import StatCard from '@/components/dashboard/StatCard';
import InstallAppPrompt from '@/components/InstallAppPrompt';
import ContratoEntregador from '@/components/ContratoEntregador';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { listarEntregas, criarPagamento, simularAprovacao, obterEmpresa } from '@/services/api';
import api from '@/services/api';

const DashboardEntregador = () => {
  const { empresa, atualizarEmpresa } = useAuth();
  const navigate = useNavigate();
  const [entregas, setEntregas] = useState([]);
  const [entregasAbertas, setEntregasAbertas] = useState([]);
  const [openPagamento, setOpenPagamento] = useState(false);
  const [openRota, setOpenRota] = useState(false);
  const [entregaSelecionada, setEntregaSelecionada] = useState(null);
  const [pagamentoAtual, setPagamentoAtual] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tabAtiva, setTabAtiva] = useState('abertas');
  const [showContrato, setShowContrato] = useState(false);
  const [contratoInfo, setContratoInfo] = useState(null);
  
  useEffect(() => {
    carregarEntregas();
    carregarEntregasAbertas();
    atualizarDadosEmpresa();
    carregarStatusContrato();
  }, []);
  
  const carregarStatusContrato = async () => {
    if (!empresa?.id) return;
    try {
      const res = await api.get(`/contratos/assinado/${empresa.id}`);
      setContratoInfo(res.data);
    } catch {
      // 404: contrato ainda não assinado
      setContratoInfo(null);
    }
  };
  
  const atualizarDadosEmpresa = async () => {
    try {
      const response = await obterEmpresa(empresa.id);
      atualizarEmpresa(response.data);
    } catch (error) {
      console.error('Erro ao atualizar empresa:', error);
    }
  };
  
  const carregarEntregas = async () => {
    try {
      const response = await listarEntregas({ entregador_id: empresa.id });
      setEntregas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar entregas');
    }
  };
  
  const carregarEntregasAbertas = async () => {
    try {
      const response = await api.get('/entregas/abertas');
      setEntregasAbertas(response.data);
    } catch (error) {
      console.error('Erro ao carregar entregas abertas:', error);
    }
  };
  
  const handleAceitarEntrega = async (entregaId) => {
    setLoading(true);
    try {
      await api.post(`/entregas/${entregaId}/aceitar`, {
        entregador_id: empresa.id
      });
      
      toast.success('Entrega aceita com sucesso!');
      carregarEntregas();
      carregarEntregasAbertas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aceitar entrega');
    } finally {
      setLoading(false);
    }
  };
  
  const abrirRota = (entrega) => {
    setEntregaSelecionada(entrega);
    setOpenRota(true);
  };
  
  const gerarLinkGoogleMaps = (entrega) => {
    if (!entrega.origem_latitude || !entrega.destino_latitude) return null;
    return `https://www.google.com/maps/dir/?api=1&origin=${entrega.origem_latitude},${entrega.origem_longitude}&destination=${entrega.destino_latitude},${entrega.destino_longitude}`;
  };
  
  const gerarLinkOpenStreetMap = (entrega) => {
    if (!entrega.origem_latitude || !entrega.destino_latitude) return null;
    return `https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=${entrega.origem_latitude},${entrega.origem_longitude};${entrega.destino_latitude},${entrega.destino_longitude}`;
  };
  
  const gerarLinkWaze = (entrega) => {
    if (!entrega.destino_latitude) return null;
    return `https://www.waze.com/ul?ll=${entrega.destino_latitude},${entrega.destino_longitude}&navigate=yes`;
  };
  
  const handleCriarPagamento = async () => {
    setLoading(true);
    
    try {
      const dados = {
        empresa_id: empresa.id,
        tipo: 'assinatura',
        metodo: 'pix'
      };
      
      const response = await criarPagamento(dados);
      setPagamentoAtual(response.data);
      toast.success('Pagamento criado! Aguardando aprovação...');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar pagamento');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSimularAprovacao = async () => {
    if (!pagamentoAtual) return;
    
    setLoading(true);
    try {
      await simularAprovacao(pagamentoAtual.id);
      toast.success('Pagamento aprovado! Assinatura ativada.');
      
      setPagamentoAtual(null);
      setOpenPagamento(false);
      atualizarDadosEmpresa();
    } catch (error) {
      toast.error('Erro ao aprovar pagamento');
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      criada: 'bg-slate-100 text-slate-800',
      atribuida: 'bg-blue-100 text-blue-800',
      aceita: 'bg-emerald-100 text-emerald-800',
      em_rota: 'bg-amber-100 text-amber-800',
      entregue: 'bg-green-100 text-green-800'
    };
    return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${badges[status]}`}>{status}</span>;
  };
  
  // Callback quando uma notificação é clicada
  const handleNotificationClick = (notification) => {
    // Recarrega entregas abertas e muda para a aba de disponíveis
    carregarEntregasAbertas();
    setTabAtiva('abertas');
  };
  
  return (
    <DashboardLayout title="Dashboard Entregador" onNotificationClick={handleNotificationClick}>
      {/* Banner de Instalação do App */}
      <InstallAppPrompt />
      
      {!empresa.assinatura_ativa && (
        <div data-testid="subscription-alert" className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800 font-semibold">
            Assinatura Inativa - Ative sua assinatura (R$ 49,90/semana) para receber entregas!
          </p>
        </div>
      )}
      
      {/* Status do contrato */}
      {!contratoInfo && (
        <div
          data-testid="contrato-pendente-alert"
          className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-center justify-between gap-4"
        >
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-yellow-600 flex-shrink-0" />
            <div>
              <p className="text-sm text-yellow-900 font-semibold">Contrato pendente de assinatura</p>
              <p className="text-xs text-yellow-700">Para receber entregas é obrigatório assinar o contrato de prestação de serviços.</p>
            </div>
          </div>
          <Button
            onClick={() => setShowContrato(true)}
            className="bg-yellow-600 hover:bg-yellow-700 text-white"
            data-testid="btn-assinar-contrato-pendente"
          >
            Assinar Agora
          </Button>
        </div>
      )}
      
      {contratoInfo && (
        <div
          data-testid="contrato-assinado-banner"
          className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center justify-between gap-4"
        >
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm text-green-900 font-semibold">
                Contrato assinado em {new Date(contratoInfo.aceite_em).toLocaleString('pt-BR')}
              </p>
              <p className="text-xs text-green-700 font-mono break-all">
                Versão {contratoInfo.versao_contrato} · Hash: {contratoInfo.hash_contrato?.substring(0, 32)}...
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowContrato(true)}
            className="border-green-600 text-green-700 hover:bg-green-100"
            data-testid="btn-ver-contrato"
          >
            <FileText className="w-4 h-4 mr-1" />
            Visualizar
          </Button>
        </div>
      )}
      
      {empresa.status_validacao !== 'aprovado' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-amber-800 font-semibold">
            Validação Pendente - Envie seus documentos para liberar seu cadastro.
          </p>
          <Button 
            size="sm" 
            className="mt-2 bg-amber-600 hover:bg-amber-700"
            onClick={() => navigate('/dashboard/entregador/documentos')}
          >
            Enviar Documentos
          </Button>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <StatCard 
          title="Entregas Abertas" 
          value={entregasAbertas.length} 
          icon={Bell} 
          color="purple" 
        />
        <StatCard title="Minhas Entregas" value={entregas.length} icon={Package} color="blue" />
        <StatCard
          title="Entregues"
          value={entregas.filter(e => e.status === 'entregue').length}
          icon={CheckCircle2}
          color="emerald"
        />
        <StatCard
          title="Em Andamento"
          value={entregas.filter(e => ['aceita', 'em_rota', 'atribuida'].includes(e.status)).length}
          icon={Clock}
          color="amber"
        />
        <StatCard
          title="Assinatura"
          value={empresa.assinatura_ativa ? 'Ativa' : 'Inativa'}
          subtitle={empresa.data_vencimento_assinatura ? 
            `Vence: ${new Date(empresa.data_vencimento_assinatura).toLocaleDateString('pt-BR')}` : 
            ''}
          icon={CreditCard}
          color={empresa.assinatura_ativa ? 'emerald' : 'red'}
        />
      </div>
      
      {/* Botão de Pagamento */}
      <div className="mb-6 flex gap-3">
        <Dialog open={openPagamento} onOpenChange={setOpenPagamento}>
          <DialogTrigger asChild>
            <Button 
              data-testid="payment-button"
              className={empresa.assinatura_ativa ? 
                "bg-blue-600 hover:bg-blue-700" : 
                "bg-emerald-600 hover:bg-emerald-700 animate-pulse"
              }
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {empresa.assinatura_ativa ? 'Renovar Assinatura' : 'Ativar Assinatura'}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assinatura Entregador</DialogTitle>
            </DialogHeader>
            
            {!pagamentoAtual ? (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">Plano Entregador</h3>
                  <p className="text-3xl font-bold text-blue-900 mb-2">R$ 49,90<span className="text-base font-normal">/semana</span></p>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>✓ Receba entregas ilimitadas</li>
                    <li>✓ Gerencie sua equipe de funcionários</li>
                    <li>✓ Acompanhe histórico completo</li>
                    <li>✓ Dashboard em tempo real</li>
                  </ul>
                </div>
                
                <Button
                  data-testid="create-subscription-button"
                  onClick={handleCriarPagamento}
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? 'Gerando PIX...' : 'Gerar PIX - R$ 49,90'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded text-center">
                  <p className="text-sm text-slate-600 mb-2">Pagamento PIX Gerado</p>
                  <p className="text-2xl font-bold text-slate-900">
                    R$ {pagamentoAtual.valor?.toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-500 mt-2">Status: {pagamentoAtual.status}</p>
                </div>
                
                {pagamentoAtual.pix_qr_code && (
                  <div className="bg-slate-100 p-3 rounded">
                    <p className="text-xs text-slate-600 break-all font-mono">
                      {pagamentoAtual.pix_qr_code}
                    </p>
                  </div>
                )}
                
                <div className="bg-amber-50 border border-amber-200 p-3 rounded">
                  <p className="text-sm text-amber-800">
                    <strong>Modo Desenvolvimento:</strong><br />
                    Simule a aprovação do pagamento clicando no botão abaixo.
                  </p>
                </div>
                
                <Button
                  data-testid="simulate-approval-button"
                  onClick={handleSimularAprovacao}
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? 'Processando...' : 'Simular Aprovação (Dev)'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {/* Tabs */}
        <div className="p-4 border-b flex gap-2">
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tabAtiva === 'abertas' 
                ? 'bg-purple-100 text-purple-700' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            onClick={() => setTabAtiva('abertas')}
          >
            <Bell className="w-4 h-4 inline mr-2" />
            Entregas Disponíveis ({entregasAbertas.length})
          </button>
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tabAtiva === 'minhas' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            onClick={() => setTabAtiva('minhas')}
          >
            <Package className="w-4 h-4 inline mr-2" />
            Minhas Entregas ({entregas.length})
          </button>
          <Button 
            variant="outline" 
            size="sm" 
            className="ml-auto"
            onClick={() => {
              carregarEntregas();
              carregarEntregasAbertas();
            }}
          >
            Atualizar
          </Button>
        </div>
        
        <div className="overflow-x-auto">
          {/* Entregas Abertas */}
          {tabAtiva === 'abertas' && (
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Descrição</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Destino</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Valor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entregasAbertas.length === 0 ? (
                  <tr><td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                    Nenhuma entrega disponível no momento
                  </td></tr>
                ) : (
                  entregasAbertas.map((e) => (
                    <tr key={e.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium">{e.cliente_nome || 'Cliente'}</p>
                      </td>
                      <td className="px-6 py-4 text-sm">{e.descricao}</td>
                      <td className="px-6 py-4">
                        <p className="text-sm">{e.destino_endereco || e.endereco_destino || '-'}</p>
                        <p className="text-xs text-slate-500">{e.origem_endereco || e.endereco_origem || '-'}</p>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {e.valor_recebimento ? (
                          <span className="text-green-600 font-medium">
                            R$ {e.valor_recebimento.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Button
                          size="sm"
                          onClick={() => handleAceitarEntrega(e.id)}
                          disabled={loading || !empresa.assinatura_ativa}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Aceitar
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
          
          {/* Minhas Entregas */}
          {tabAtiva === 'minhas' && (
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Descrição</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Destino</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Data</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entregas.length === 0 ? (
                  <tr><td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                    {!empresa.assinatura_ativa ? 
                      'Ative sua assinatura para começar a receber entregas' : 
                      'Nenhuma entrega atribuída. Aceite entregas disponíveis!'
                    }
                  </td></tr>
                ) : (
                  entregas.map((e) => (
                    <tr key={e.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm">{e.descricao}</td>
                      <td className="px-6 py-4 text-sm">{e.destino_endereco || e.endereco_destino}</td>
                      <td className="px-6 py-4">{getStatusBadge(e.status)}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(e.criado_em).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4">
                        {e.destino_latitude && e.origem_latitude && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => abrirRota(e)}
                            className="border-blue-600 text-blue-600 hover:bg-blue-50"
                          >
                            <Navigation className="w-4 h-4 mr-1" />
                            Rota
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
      
      {/* Modal de Rota */}
      <Dialog open={openRota} onOpenChange={setOpenRota}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abrir Rota de Navegação</DialogTitle>
          </DialogHeader>
          
          {entregaSelecionada && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Origem</p>
                    <p className="text-sm font-medium">{entregaSelecionada.origem_endereco}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Navigation className="w-4 h-4 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Destino</p>
                    <p className="text-sm font-medium">{entregaSelecionada.destino_endereco}</p>
                  </div>
                </div>
              </div>
              
              <div className="grid gap-2">
                <a
                  href={gerarLinkGoogleMaps(entregaSelecionada)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 h-11 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir no Google Maps
                </a>
                
                <a
                  href={gerarLinkWaze(entregaSelecionada)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 h-11 px-4 bg-cyan-600 hover:bg-cyan-700 text-white rounded-md font-medium transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir no Waze
                </a>
                
                <a
                  href={gerarLinkOpenStreetMap(entregaSelecionada)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 h-11 px-4 bg-slate-600 hover:bg-slate-700 text-white rounded-md font-medium transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir no OpenStreetMap
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Modal de Contrato (visualizar/assinar) */}
      <ContratoEntregador
        isOpen={showContrato}
        empresaId={empresa?.id}
        onClose={() => setShowContrato(false)}
        onAssinado={() => {
          carregarStatusContrato();
          atualizarDadosEmpresa();
        }}
      />
    </DashboardLayout>
  );
};

export default DashboardEntregador;
